/**
 * Vorgangsarten — the kind of a Vorgang (process).
 *
 * Kürzel are kept verbatim as the display labels (per product decision):
 *   NKG, JG, GAB, Sonstige.
 *
 * „Allgemein" und „SG-Service" wurden auf PO-Wunsch (26.07.2026) aus der
 * Auswahl entfernt — sie sind für **neue** Vorgänge nicht mehr wählbar.
 * Bestands-Vorgänge behalten ihre Art unverändert: `artOptions()` in
 * `components/vorgang/detail/overviewHead.ts` hängt eine nicht (mehr) gelistete
 * Art vorne an das Dropdown, damit ein gesetzter Wert nicht aus der Liste fällt
 * und beim Speichern nicht verloren geht.
 *
 * „Sonstige" ist seither die DEFAULT-Art neu erstellter Vorgänge und bleibt
 * zugleich der Auffang-Wert (Review B4) — wählbar im Erstell-Popup UND im
 * Art-Dropdown der Detailseite.
 *
 * **Art UND Betreff** (PO 02.08.2026: „NKG, GAB, JG haben auch einen Betreff —
 * nebeneinander anzeigen"): Jede Art kann einen Betreff tragen; die Detailseite
 * zeigt Art-Chip und Betreff nebeneinander. Wird keiner eingegeben, springt bei
 * NKG/JG/GAB das Kürzel als Titel ein, damit Listen und Mail-Betreffe nicht leer
 * laufen. Bis 28.07.2026 galt die schärfere Regel „die Art IST der Betreff" —
 * daher tragen viele Bestands-Vorgänge das Kürzel als Titel (Migration
 * VorgangsartAlsBetreff); dort erscheint das Betreff-Feld leer und kann
 * nachgetragen werden.
 *
 * Nicht in dieser Liste, weil rein programmatisch gesetzt: „NKG" über
 * `services/menschenFuersUnternehmenService.ts` und „Rückruf" über
 * `components/telephony/callRueckrufVorgang.ts`.
 *
 * Source of truth lives here in the frontend; the backend persists the raw
 * string in `Vorgang.vorgangsart` (nullable varchar), so adding/renaming a
 * value never needs a schema change. Das Backend spiegelt Liste und
 * Betreff-Regel in [apps/api/src/vorgang/vorgangArt.ts], damit der Betreff
 * unabhängig vom Client final normalisiert wird.
 */
export const VORGANG_ARTEN = ["NKG", "JG", "GAB", "Sonstige"] as const;

export type VorgangArt = (typeof VORGANG_ARTEN)[number];

/** Die einzige Art mit frei eingegebenem Betreff. */
export const FREITEXT_ART: VorgangArt = "Sonstige";

/**
 * Kürzel → ausgeschriebenes Wort für die ANZEIGE (PO 13.09.2026: „JG =
 * Jahresgespräch"). Ein Kürzel erklärt sich nur dem, der es kennt; in der
 * Kopfzeile des Vorgangs steht die Art als einziges Wort neben dem Kundennamen
 * und muss allein verständlich sein.
 *
 * NUR ANZEIGE: gespeichert bleibt das Kürzel ({@link VORGANG_ARTEN}) — es steht
 * in `Vorgang.vorgangsart`, in den Mail-Regeln, in der Telefonie und im
 * Backend. Eine Umbenennung der Werte wäre eine Datenmigration.
 *
 * ABSICHTLICH UNVOLLSTÄNDIG: NKG und GAB bleiben beim Kürzel, bis der PO ihre
 * Langformen nennt — dann kommen hier zwei Zeilen dazu, sonst nichts. Ein
 * geratenes Wort wäre schlimmer als das Kürzel, das die Kollegen kennen.
 * „Sonstige" ist schon ein Wort.
 */
const ART_LANGFORM: Record<string, string> = {
    JG: "Jahresgespräch",
};

/**
 * Das ANZUZEIGENDE Wort einer Vorgangsart: die Langform, wo es eine gibt, sonst
 * der Wert selbst — also auch für Arten außerhalb des Katalogs („Rückruf") und
 * für Bestands-Arten. Leere Eingabe bleibt leer.
 *
 * Nur für die Darstellung; wer speichert oder vergleicht, arbeitet weiter mit
 * dem Kürzel. Benutzt wird die Funktion bislang allein in der Kopfzeile des
 * Vorgangs.
 */
export function artLangform(art: string | null | undefined): string {
    const a = (art ?? "").trim();
    return ART_LANGFORM[a] ?? a;
}

/**
 * Default-Vorgangsart für **neu erstellte** Vorgänge: das Erstell-Popup und das
 * Vorgang-Formular belegen die Art damit vor, statt den User zur Auswahl zu
 * zwingen. Der Kopf zeigt dann „VORGANG: SONSTIGE".
 */
export const DEFAULT_VORGANG_ART: VorgangArt = FREITEXT_ART;

/**
 * Springt diese Art als Betreff ein, solange keiner eingegeben ist? Gilt für die
 * KATALOG-Arten außer „Sonstige" (NKG, JG, GAB). Bewusst NICHT für Arten
 * außerhalb des Katalogs: „Rückruf" (Telefonie) und ausgelaufene Bestands-Arten
 * haben sprechende Titel („Rückruf Müller"), die erhalten bleiben.
 */
export function hatAutomatischenBetreff(art?: string | null): boolean {
    const a = (art ?? "").trim();
    return !!a && a !== FREITEXT_ART && (VORGANG_ARTEN as readonly string[]).includes(a);
}

/**
 * Der zu speichernde Betreff (`Vorgang.titel`): der eingegebene Freitext —
 * und nur ersatzweise (leer) das Kürzel der Art. Das Backend normalisiert
 * dieselbe Regel final (applyBetreffRegel).
 */
export function betreffFuerArt(art: string | null | undefined, freitext: string): string {
    const eigener = freitext.trim();
    if (eigener) return eigener;
    return hatAutomatischenBetreff(art) ? (art as string).trim() : '';
}

/**
 * Verdient diese Art einen eigenen Chip neben dem Betreff? „Sonstige" ist der
 * Auffang-Wert und sagt nichts aus — dort steht nur der Betreff (PO 03.08.2026).
 */
export function zeigtArtChip(art: string | null | undefined): boolean {
    const a = (art ?? "").trim();
    return !!a && a !== FREITEXT_ART;
}

/**
 * Der ANZUZEIGENDE Betreff neben dem Art-Chip: Bestands-Vorgänge tragen das
 * Kürzel als Titel (Alt-Regel „die Art IST der Betreff") — dort steht die Art
 * schon im Chip, das Betreff-Feld bleibt also leer und lädt zum Nachtragen ein.
 */
export function betreffOhneArt(art: string | null | undefined, titel: string | null | undefined): string {
    const t = (titel ?? "").trim();
    const a = (art ?? "").trim();
    return t && a && t === a ? "" : t;
}
